import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import Layout from '../layout/Layout';
import LoadingSpinner from '../common/LoadingSpinner';
import toast from 'react-hot-toast';
import { industriesAPI } from '../../services/api';

const IndustryMaster = () => {
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingIndustry, setEditingIndustry] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingIndustry, setDeletingIndustry] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    customFields: []
  });
  const [submitting, setSubmitting] = useState(false);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    fetchIndustries();
  }, []);

  const fetchIndustries = async () => {
    try {
      setLoading(true);
      const response = await industriesAPI.getAll({ isActive: undefined });
      setIndustries(response.data.industries || []);
    } catch (error) {
      toast.error('Failed to load industries');
      console.error('Fetch industries error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDefaults = async () => {
    if (!window.confirm('This will seed the standard industries and their custom fields. Continue?')) return;
    
    try {
      setSeeding(true);
      const res = await industriesAPI.seedDefaults();
      toast.success(res.data.message || 'Seeded successfully');
      fetchIndustries();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Seeding failed');
    } finally {
      setSeeding(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Industry name is required');
      return;
    }

    try {
      setSubmitting(true);
      if (editingIndustry) {
        await industriesAPI.update(editingIndustry._id, formData);
        toast.success('Industry updated successfully');
      } else {
        await industriesAPI.create(formData);
        toast.success('Industry created successfully');
      }

      setShowModal(false);
      setEditingIndustry(null);
      setFormData({ name: '', description: '', customFields: [] });
      fetchIndustries();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save industry');
      console.error('Save industry error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (industry) => {
    setEditingIndustry(industry);
    setFormData({
      name: industry.name,
      description: industry.description || '',
      customFields: industry.customFields || []
    });
    setShowModal(true);
  };

  const handleDelete = (industry) => {
    setDeletingIndustry(industry);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingIndustry) return;

    try {
      await industriesAPI.delete(deletingIndustry._id);
      toast.success('Industry deleted successfully');
      fetchIndustries();
      setShowDeleteModal(false);
      setDeletingIndustry(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete industry');
      console.error('Delete industry error:', error);
    }
  };

  const toggleStatus = async (industry) => {
    try {
      await industriesAPI.toggleStatus(industry._id);
      toast.success(`Industry ${industry.isActive ? 'deactivated' : 'activated'} successfully`);
      fetchIndustries();
    } catch (error) {
      toast.error('Failed to update industry status');
      console.error('Toggle status error:', error);
    }
  };

  const filteredIndustries = industries.filter(industry =>
    industry.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (industry.description && industry.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <LoadingSpinner size="large" text="Loading industries..." />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="mb-4 sm:mb-0">
                <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  Industry Master
                </h1>
                <p className="mt-2 text-gray-600 text-lg">
                  Manage industry types and custom fields for your organization
                </p>
              </div>
              <div className="flex-shrink-0 flex items-center space-x-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="button"
                  onClick={handleSeedDefaults}
                  //disabled={seeding || industries.length > 0}
                  className="inline-flex items-center px-6 py-3 border border-gray-300 rounded-2xl shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={industries.length > 0 ? "Seed available only when list is empty" : "Seed standard industries"}
                >
                  <ArrowPathIcon className={`h-5 w-5 mr-2 ${seeding ? 'animate-spin' : ''}`} />
                  {seeding ? 'Seeding...' : 'Seed Defaults'}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }}
                  whileTap={{ scale: 0.95 }}
                  type="button"
                  onClick={() => {
                    setEditingIndustry(null);
                    setFormData({ name: '', description: '', customFields: [] });
                    setShowModal(true);
                  }}
                  className="inline-flex items-center px-6 py-3 border border-transparent rounded-2xl shadow-lg text-base font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Add Industry
                </motion.button>
              </div>
            </div>
          </div>

        {/* Industries Table */}
        <div className="mt-8">
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100/50">
              <h2 className="text-lg font-semibold text-gray-900">Industries</h2>
              <p className="text-sm text-gray-600 mt-1">Manage your industry types and custom fields</p>
            </div>

            {/* Search */}
            <div className="px-6 py-4 border-b border-gray-100/50">
              <div className="relative max-w-md">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search industries..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-50/50 border border-gray-200/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200 placeholder-gray-500"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              {/* Card Grid Layout */}
              {filteredIndustries.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
                  {filteredIndustries.map((industry, index) => (
                    <motion.div
                      key={industry._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      className="group bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-2xl border border-white/50 overflow-hidden transition-all duration-300 hover:-translate-y-1"
                    >
                      {/* Card Header */}
                      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-4 relative overflow-hidden">
                        <div className="absolute inset-0 bg-black/10"></div>
                        <div className="relative flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30">
                              <span className="text-white font-bold text-lg">
                                {industry.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <h3 className="text-lg font-bold text-white">{industry.name}</h3>
                              <p className="text-white/80 text-xs">Industry Type</p>
                            </div>
                          </div>
                          <div className="flex items-center">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              industry.isActive
                                ? 'bg-emerald-500/20 text-emerald-100 border border-emerald-400/30'
                                : 'bg-gray-500/20 text-gray-100 border border-gray-400/30'
                            }`}>
                              <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                                industry.isActive ? 'bg-emerald-400' : 'bg-gray-400'
                              }`}></div>
                              {industry.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Card Content */}
                      <div className="p-4 space-y-3">
                        {/* Description */}
                        <div>
                          <p className="text-gray-600 text-sm leading-relaxed">
                            {industry.description || 'No description provided for this industry.'}
                          </p>
                        </div>

                        {/* Custom Fields Section */}
                        <div className="border-t border-gray-100 pt-3">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-semibold text-gray-800">Custom Fields</h4>
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                              {industry.customFields?.length || 0} fields
                            </span>
                          </div>

                          {industry.customFields && industry.customFields.length > 0 ? (
                            <div className="space-y-2 max-h-24 overflow-y-auto">
                              {industry.customFields.map((field, fieldIndex) => (
                                <div key={fieldIndex} className="flex items-center justify-between bg-gray-50/50 rounded-lg p-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center space-x-2">
                                      <span className="text-sm font-medium text-gray-800 truncate">
                                        {field.name}
                                      </span>
                                      {field.required && (
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                                          Required
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center space-x-2 mt-0.5">
                                      <span className="text-xs text-gray-500 capitalize">{field.type}</span>
                                      <span className="text-xs text-gray-400">•</span>
                                      <span className="text-xs text-gray-500 capitalize">{field.module}</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-4 text-gray-400">
                              <PlusIcon className="h-6 w-6 mx-auto mb-1 opacity-50" />
                              <p className="text-xs">No custom fields</p>
                            </div>
                          )}
                        </div>

                        {/* Metadata */}
                        <div className="flex items-center justify-between text-xs text-gray-500 border-t border-gray-100 pt-2">
                          <span>Created {new Date(industry.createdAt).toLocaleDateString('en-GB')}</span>
                          <span>{industry.customFields?.length || 0} custom fields</span>
                        </div>
                      </div>

                      {/* Card Actions */}
                      <div className="px-4 pb-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleEdit(industry)}
                              className="flex items-center space-x-1 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors duration-200 text-xs font-medium"
                            >
                              <PencilIcon className="h-3.5 w-3.5" />
                              <span>Edit</span>
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => toggleStatus(industry)}
                              className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg transition-colors duration-200 text-xs font-medium ${
                                industry.isActive
                                  ? 'bg-orange-50 text-orange-700 hover:bg-orange-100'
                                  : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                              }`}
                            >
                              <EyeIcon className="h-3.5 w-3.5" />
                              <span>{industry.isActive ? 'Deactivate' : 'Activate'}</span>
                            </motion.button>
                          </div>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleDelete(industry)}
                            className="flex items-center space-x-1 px-3 py-1.5 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors duration-200 text-xs font-medium"
                          >
                            <TrashIcon className="h-3.5 w-3.5" />
                            <span>Delete</span>
                          </motion.button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 px-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl flex items-center justify-center mb-6 shadow-lg">
                    <MagnifyingGlassIcon className="h-10 w-10 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {searchTerm ? 'No industries found' : 'No industries yet'}
                  </h3>
                  <p className="text-gray-500 text-center max-w-md mb-6">
                    {searchTerm
                      ? 'Try adjusting your search terms or clear the search to see all industries.'
                      : 'Get started by creating your first industry type to organize your business data.'
                    }
                  </p>
                  {!searchTerm && (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        setEditingIndustry(null);
                        setFormData({ name: '', description: '', customFields: [] });
                        setShowModal(true);
                      }}
                      className="inline-flex items-center px-6 py-3 border border-transparent rounded-2xl shadow-lg text-base font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 transition-all duration-200"
                    >
                      <PlusIcon className="h-5 w-5 mr-2" />
                      Create Your First Industry
                    </motion.button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modal */}
        <AnimatePresence>
          {showModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md"
              onClick={() => {
                setShowModal(false);
                setEditingIndustry(null);
                setFormData({ name: '', description: '', customFields: [] });
              }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-white/20"
                onClick={(e) => e.stopPropagation()}
              >
                <form onSubmit={handleSubmit}>
                  <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-8 py-6 relative overflow-hidden">
                    <div className="absolute inset-0 bg-black/10"></div>
                    <div className="relative flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl border border-white/30">
                          {editingIndustry ? (
                            <PencilIcon className="h-7 w-7 text-white" />
                          ) : (
                            <PlusIcon className="h-7 w-7 text-white" />
                          )}
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-white">
                            {editingIndustry ? 'Edit Industry' : 'Create New Industry'}
                          </h3>
                          <p className="text-white/80 mt-1">
                            {editingIndustry ? 'Update industry details and custom fields' : 'Add a new industry type to your system'}
                          </p>
                        </div>
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.1, rotate: 90 }}
                        whileTap={{ scale: 0.9 }}
                        type="button"
                        onClick={() => {
                          setShowModal(false);
                          setEditingIndustry(null);
                          setFormData({ name: '', description: '', customFields: [] });
                        }}
                        className="p-2 hover:bg-white/20 rounded-xl transition-all duration-200 border border-white/30"
                      >
                        <XMarkIcon className="h-6 w-6 text-white" />
                      </motion.button>
                    </div>
                  </div>

                  <div className="p-8 space-y-8">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-3">
                          Industry Name <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="w-full px-4 py-4 bg-gray-50/50 border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 focus:bg-white/50 transition-all duration-200 placeholder-gray-500 text-gray-900 shadow-sm"
                            placeholder="Enter industry name"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-3">
                          Description
                        </label>
                        <textarea
                          rows={4}
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          className="w-full px-4 py-4 bg-gray-50/50 border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 focus:bg-white/50 transition-all duration-200 placeholder-gray-500 text-gray-900 shadow-sm resize-none"
                          placeholder="Describe this industry type (optional)"
                        />
                      </div>
                    </div>

                    {/* Custom Fields Section */}
                    <div className="bg-gradient-to-br from-purple-50/50 via-pink-50/50 to-indigo-50/50 rounded-2xl p-6 border border-purple-100/50 backdrop-blur-sm">
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                          <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl shadow-lg">
                            <PlusIcon className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <h4 className="text-xl font-bold text-gray-800">Custom Fields</h4>
                            <p className="text-sm text-gray-600 mt-1">Add custom fields for leads and customers</p>
                          </div>
                        </div>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          type="button"
                          onClick={() => setFormData({
                            ...formData,
                            customFields: [...formData.customFields, {
                              name: '',
                              type: 'text',
                              module: 'lead',
                              required: false,
                              options: [],
                              order: formData.customFields.length
                            }]
                          })}
                          className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-2xl hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all duration-200 text-sm font-semibold shadow-lg"
                        >
                          Add Field
                        </motion.button>
                      </div>

                      <div className="space-y-4">
                        {formData.customFields.map((field, index) => (
                          <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Field Name *
                                </label>
                                <input
                                  type="text"
                                  value={field.name}
                                  onChange={(e) => {
                                    const updatedFields = [...formData.customFields];
                                    updatedFields[index].name = e.target.value;
                                    setFormData({ ...formData, customFields: updatedFields });
                                  }}
                                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                  placeholder="Enter field name"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Field Type *
                                </label>
                                <select
                                  value={field.type}
                                  onChange={(e) => {
                                    const updatedFields = [...formData.customFields];
                                    updatedFields[index].type = e.target.value;
                                    // Reset options if not dropdown
                                    if (!['dropdown'].includes(e.target.value)) {
                                      updatedFields[index].options = [];
                                    }
                                    setFormData({ ...formData, customFields: updatedFields });
                                  }}
                                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                >
                                  <option value="text">Text</option>
                                  <option value="number">Number</option>
                                  <option value="email">Email</option>
                                  <option value="phone">Phone</option>
                                  <option value="date">Date</option>
                                  <option value="dropdown">Dropdown</option>
                                  <option value="textarea">Textarea</option>
                                  <option value="checkbox">Checkbox</option>
                                  <option value="url">URL</option>
                                </select>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Module *
                                </label>
                                <select
                                  value={field.module}
                                  onChange={(e) => {
                                    const updatedFields = [...formData.customFields];
                                    updatedFields[index].module = e.target.value;
                                    setFormData({ ...formData, customFields: updatedFields });
                                  }}
                                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                >
                                  <option value="lead">Lead</option>
                                  <option value="customer">Customer</option>
                                </select>
                              </div>
                            </div>

                            {field.type === 'dropdown' && (
                              <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Options
                                </label>
                                <div className="space-y-3">
                                  {/* Display existing options as chips */}
                                  {field.options && field.options.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                      {field.options.map((option, optIndex) => (
                                        <span key={optIndex} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800">
                                          {option}
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const updatedFields = [...formData.customFields];
                                              updatedFields[index].options = updatedFields[index].options.filter((_, i) => i !== optIndex);
                                              setFormData({ ...formData, customFields: updatedFields });
                                            }}
                                            className="ml-2 text-purple-600 hover:text-purple-800 focus:outline-none"
                                          >
                                            <XMarkIcon className="h-3 w-3" />
                                          </button>
                                        </span>
                                      ))}
                                    </div>
                                  )}

                                  {/* Add new option input */}
                                  <div className="flex space-x-2">
                                    <input
                                      type="text"
                                      placeholder="Add new option"
                                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                                      onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          const value = e.target.value.trim();
                                          if (value) {
                                            const updatedFields = [...formData.customFields];
                                            if (!updatedFields[index].options) {
                                              updatedFields[index].options = [];
                                            }
                                            updatedFields[index].options.push(value);
                                            setFormData({ ...formData, customFields: updatedFields });
                                            e.target.value = '';
                                          }
                                        }
                                      }}
                                    />
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        const input = e.target.previousElementSibling;
                                        const value = input.value.trim();
                                        if (value) {
                                          const updatedFields = [...formData.customFields];
                                          if (!updatedFields[index].options) {
                                            updatedFields[index].options = [];
                                          }
                                          updatedFields[index].options.push(value);
                                          setFormData({ ...formData, customFields: updatedFields });
                                          input.value = '';
                                        }
                                      }}
                                      className="px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 text-sm font-medium"
                                    >
                                      Add
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}

                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id={`required-${index}`}
                                  checked={field.required}
                                  onChange={(e) => {
                                    const updatedFields = [...formData.customFields];
                                    updatedFields[index].required = e.target.checked;
                                    setFormData({ ...formData, customFields: updatedFields });
                                  }}
                                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                                />
                                <label htmlFor={`required-${index}`} className="text-sm text-gray-700">
                                  Required field
                                </label>
                              </div>

                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                type="button"
                                onClick={() => {
                                  const updatedFields = formData.customFields.filter((_, i) => i !== index);
                                  setFormData({ ...formData, customFields: updatedFields });
                                }}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </motion.button>
                            </div>
                          </div>
                        ))}

                        {formData.customFields.length === 0 && (
                          <div className="text-center py-8 text-gray-500">
                            <PlusIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                            <p className="text-sm">No custom fields added yet. Click "Add Field" to create custom fields for this industry.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50/50 px-8 py-6 rounded-b-3xl flex justify-end space-x-4 border-t border-gray-100/50">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        setEditingIndustry(null);
                        setFormData({ name: '', description: '', customFields: [] });
                      }}
                      className="px-8 py-3 border border-gray-300/50 rounded-2xl text-gray-700 hover:bg-white/50 transition-all duration-200 font-semibold shadow-sm"
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={submitting}
                      className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                    >
                      {submitting ? (
                        <div className="flex items-center">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                          Saving...
                        </div>
                      ) : (
                        editingIndustry ? 'Update Industry' : 'Create Industry'
                      )}
                    </motion.button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Delete Confirmation Modal */}
        <AnimatePresence>
          {showDeleteModal && deletingIndustry && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md"
              onClick={() => setShowDeleteModal(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl max-w-md w-full border border-white/20"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-8">
                  <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-red-500 to-pink-500 rounded-3xl shadow-lg">
                    <TrashIcon className="w-8 h-8 text-white" />
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 text-center mb-3">
                    Delete Industry
                  </h3>

                  <p className="text-gray-600 text-center mb-8 leading-relaxed">
                    Are you sure you want to delete <span className="font-semibold text-gray-900">"{deletingIndustry.name}"</span>?
                    This action cannot be undone and will remove all associated custom fields.
                  </p>

                  <div className="flex space-x-4">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        setShowDeleteModal(false);
                        setDeletingIndustry(null);
                      }}
                      className="flex-1 px-6 py-3 border border-gray-300/50 rounded-2xl text-gray-700 hover:bg-white/50 transition-all duration-200 font-semibold shadow-sm"
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={confirmDelete}
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-2xl hover:from-red-700 hover:to-pink-700 transition-all duration-200 font-semibold shadow-lg"
                    >
                      Delete
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        </div>
      </div>
    </Layout>
  );
};

export default IndustryMaster;